import { setPlayIconState } from "../../../partials/actions/setPlayIconState";
import { chatDebugLog } from "../../../partials/helpers/chatDebug";

let worker = null;
let audioContext = null;
let activeSourceNodes = [];
let isKokoroSpeaking = false;
let kokoroInitPromise = null;
let currentResolveCallback = null;
let currentPlayIcon = null;
let nextPlayTime = 0;
let isGenerationDone = false;
let speakQueue = []; // Queue for fire-and-forget auto-play
let silenceWatchdog = null; // Timeout to force-stop if worker stalls
let sessionId = 0; // Incremented on each speakKokoroText — guards old onended callbacks

function getPluginUrl() {
    let pluginUrl = '/app/plugins/growtype-chat/public/';
    if (typeof growtype_chat_ajax !== 'undefined' && growtype_chat_ajax.plugin_url) {
        pluginUrl = growtype_chat_ajax.plugin_url;
    }
    return pluginUrl;
}

function getUploadsModelsUrl() {
    let url = '/app/uploads/growtype-chat/models/';
    if (typeof growtype_chat_ajax !== 'undefined' && growtype_chat_ajax.uploads_models_url) {
        url = growtype_chat_ajax.uploads_models_url;
    }
    return url;
}

export async function initKokoro() {
    if (worker) return kokoroInitPromise;

    const pluginUrl = getPluginUrl();
    const workerUrl = pluginUrl + 'scripts/kokoroWorker.js';
    chatDebugLog('kokoro','[Kokoro] Creating worker at:', workerUrl);
    worker = new Worker(workerUrl, { type: 'module' });

    kokoroInitPromise = new Promise((resolve, reject) => {
        worker.addEventListener('message', (e) => {
            const { status, error, audio, sampleRate } = e.data;
            chatDebugLog('kokoro','[Kokoro] Worker message:', status, '| isKokoroSpeaking:', isKokoroSpeaking, '| isGenerationDone:', isGenerationDone, '| activeNodes:', activeSourceNodes.length);

            if (status === 'ready') {
                chatDebugLog('kokoro','[Kokoro] Worker ready');
                resolve(true);
            } else if (status === 'error') {
                console.error('[Kokoro Worker] Error:', error);
                stopKokoroSpeech(currentPlayIcon, currentResolveCallback);
            } else if (status === 'chunk') {
                playAudioChunk(audio, sampleRate);
            } else if (status === 'done') {
                chatDebugLog('kokoro','[Kokoro] Generation done. activeNodes:', activeSourceNodes.length, '| isKokoroSpeaking:', isKokoroSpeaking);
                isGenerationDone = true;
                if (activeSourceNodes.length === 0 && isKokoroSpeaking) {
                    chatDebugLog('kokoro','[Kokoro] No active nodes after done — stopping immediately');
                    stopKokoroSpeech(currentPlayIcon, currentResolveCallback);
                } else if (isKokoroSpeaking && audioContext) {
                    const msUntilEnd = Math.max(0, nextPlayTime - audioContext.currentTime) * 1000 + 500;
                    chatDebugLog('kokoro','[Kokoro] Scheduling fallback cleanup in', msUntilEnd.toFixed(0), 'ms');
                    const snapIcon = currentPlayIcon;
                    const snapCb   = currentResolveCallback;
                    setTimeout(() => {
                        chatDebugLog('kokoro','[Kokoro] Fallback timeout fired. isKokoroSpeaking:', isKokoroSpeaking, '| isGenerationDone:', isGenerationDone, '| activeNodes:', activeSourceNodes.length);
                        if (isKokoroSpeaking && isGenerationDone) {
                            chatDebugLog('kokoro','[Kokoro] Fallback cleanup executing stopKokoroSpeech');
                            stopKokoroSpeech(snapIcon, snapCb);
                        }
                    }, msUntilEnd);
                }
            }
        });
    });

    const url = getUploadsModelsUrl();
    worker.postMessage({ action: 'init', modelsUrl: url });
    return kokoroInitPromise;
}

function playAudioChunk(audioData, sampleRate) {
    if (!isKokoroSpeaking) {
        chatDebugLog('kokoro','[Kokoro] playAudioChunk skipped — not speaking');
        return;
    }

    // Cancel any silence watchdog — new audio is arriving
    if (silenceWatchdog) {
        clearTimeout(silenceWatchdog);
        silenceWatchdog = null;
        chatDebugLog('kokoro','[Kokoro] Silence watchdog cleared — new chunk arrived');
    }

    // Switch from spinner → pause icon the moment audio actually starts
    setPlayIconState(currentPlayIcon, true);

    let maxVal = 0;
    for (let i = 0; i < audioData.length; i++) {
        const abs = Math.abs(audioData[i]);
        if (abs > maxVal) maxVal = abs;
    }
    if (maxVal > 0) {
        const scale = 0.85 / maxVal;
        for (let i = 0; i < audioData.length; i++) {
            audioData[i] *= scale;
        }
    }

    const audioBuffer = audioContext.createBuffer(1, audioData.length, sampleRate);
    audioBuffer.getChannelData(0).set(audioData);
    const source = audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioContext.destination);

    const currentTime = audioContext.currentTime;
    if (nextPlayTime < currentTime) nextPlayTime = currentTime + 0.05;

    source.start(nextPlayTime);
    nextPlayTime += audioBuffer.duration;
    activeSourceNodes.push(source);

    chatDebugLog('kokoro','[Kokoro] Chunk scheduled. duration:', audioBuffer.duration.toFixed(2), 's | maxVal:', maxVal.toFixed(4), '| activeNodes:', activeSourceNodes.length, '| nextPlayTime:', nextPlayTime.toFixed(2));

    const capturedSessionId = sessionId; // capture to guard against stale callbacks
    source.onended = () => {
        // Discard if this onended belongs to a previous session (old stopped nodes)
        if (capturedSessionId !== sessionId) {
            chatDebugLog('kokoro','[Kokoro] source.onended ignored — stale session', capturedSessionId, 'vs current', sessionId);
            return;
        }
        const index = activeSourceNodes.indexOf(source);
        if (index > -1) {
            activeSourceNodes.splice(index, 1);
        }
        chatDebugLog('kokoro','[Kokoro] source.onended fired. remaining activeNodes:', activeSourceNodes.length, '| isGenerationDone:', isGenerationDone, '| isKokoroSpeaking:', isKokoroSpeaking);
        if (activeSourceNodes.length === 0 && isKokoroSpeaking) {
            if (isGenerationDone) {
                chatDebugLog('kokoro','[Kokoro] Last node ended + generation done → stopKokoroSpeech');
                stopKokoroSpeech(currentPlayIcon, currentResolveCallback);
            } else {
                // Worker is still generating more chunks — show spinner during the silence gap.
                // Start a watchdog: if no new chunk arrives in 15s, the worker has stalled.
                chatDebugLog('kokoro','[Kokoro] All audio played but generation not done — showing spinner + starting watchdog');
                setPlayIconState(currentPlayIcon, 'loading');
                const snapIcon = currentPlayIcon;
                const snapCb   = currentResolveCallback;
                silenceWatchdog = setTimeout(() => {
                    silenceWatchdog = null;
                    if (isKokoroSpeaking && activeSourceNodes.length === 0) {
                        chatDebugLog('kokoro','[Kokoro] Silence watchdog: no new audio in 15s — forcing stop');
                        stopKokoroSpeech(snapIcon, snapCb);
                    }
                }, 15000);
            }
        }
    };
}


export function stopKokoroSpeech(playIcon = null, resolveCallback = null) {
    chatDebugLog('kokoro','[Kokoro] stopKokoroSpeech called. playIcon:', playIcon, '| currentPlayIcon:', currentPlayIcon, '| isKokoroSpeaking:', isKokoroSpeaking);
    if (silenceWatchdog) {
        clearTimeout(silenceWatchdog);
        silenceWatchdog = null;
    }
    isKokoroSpeaking = false;
    isGenerationDone = false;
    if (worker) worker.postMessage({ action: 'stop' });

    activeSourceNodes.forEach(node => {
        try { node.stop(); } catch (e) { }
    });
    activeSourceNodes = [];

    // Fall back to currentPlayIcon if no explicit arg passed (e.g. mid-playback interrupt).
    // If both are empty, scan the DOM for any stuck playing/loading icon.
    let iconToClean = (playIcon && playIcon.length) ? playIcon : currentPlayIcon;
    if (!iconToClean || !iconToClean.length) {
        chatDebugLog('kokoro','[Kokoro] iconToClean is empty — falling back to DOM selector');
        iconToClean = jQuery('.g-chatform-message-play.is-playing, .g-chatform-message-play.is-loading');
    }
    chatDebugLog('kokoro','[Kokoro] Calling setPlayIconState(false) on icon. length:', iconToClean ? iconToClean.length : 'null');
    setPlayIconState(iconToClean, false);

    currentPlayIcon = null;

    if (resolveCallback) resolveCallback();
    else if (currentResolveCallback) currentResolveCallback();

    currentResolveCallback = null;

    // Process next item in the auto-play queue
    if (speakQueue.length > 0) {
        const next = speakQueue.shift();
        speakKokoroText(next.text, next.playIcon, next.resolveCallback, next.voice, true);
    }
}

export async function speakKokoroText(text, playIcon, resolveCallback = null, voice = 'af_nicole', fromQueue = false) {
    chatDebugLog('kokoro','[Kokoro] Raw text passed to TTS (' + text.length + ' chars):', text);
    text = stripHTML(text);
    text = stripEmojis(text);
    chatDebugLog('kokoro','[Kokoro] Text after cleanup (' + text.length + ' chars):', text);
    chatDebugLog('kokoro','[Kokoro] speakKokoroText called. isKokoroSpeaking:', isKokoroSpeaking, '| voice:', voice, '| fromQueue:', fromQueue);
    if (isKokoroSpeaking) {
        chatDebugLog('kokoro','[Kokoro] Already speaking — stopping first');
        stopKokoroSpeech();
    }
    // Only clear queue on manual user trigger (not when advancing the queue itself)
    if (!fromQueue) {
        speakQueue.forEach(item => {
            setPlayIconState(item.playIcon, false);
            if (item.resolveCallback) item.resolveCallback();
        });
        speakQueue = [];
    }

    isKokoroSpeaking = true;
    isGenerationDone = false;
    sessionId++; // new session — old onended callbacks will be discarded
    currentPlayIcon = playIcon;
    currentResolveCallback = resolveCallback;

    chatDebugLog('kokoro','[Kokoro] Setting icon to loading. playIcon:', playIcon);
    setPlayIconState(playIcon, 'loading');

    try {
        await initKokoro();
        if (!audioContext) audioContext = new (window.AudioContext || window.webkitAudioContext)();
        if (audioContext.state === 'suspended') await audioContext.resume();
        chatDebugLog('kokoro','[Kokoro] AudioContext state:', audioContext.state, '| currentTime:', audioContext.currentTime);

        nextPlayTime = audioContext.currentTime + 0.05;
        chatDebugLog('kokoro','[Kokoro] Posting speak to worker. nextPlayTime:', nextPlayTime.toFixed(2));
        worker.postMessage({ action: 'speak', text: text, voice: voice });
    } catch (e) {
        console.error('[Kokoro] Playback error', e);
        stopKokoroSpeech(playIcon, resolveCallback);
    }
}

/**
 * Queue text for playback without interrupting what's currently playing.
 * Used by auto-play so messages play back-to-back without stopping each other.
 */
export async function queueKokoroText(text, playIcon, resolveCallback = null, voice = 'af_nicole') {
    if (!isKokoroSpeaking) {
        // Nothing playing — start immediately (speakKokoroText handles cleanup)
        return speakKokoroText(text, playIcon, resolveCallback, voice);
    }
    // Something is playing — append to queue (speakKokoroText cleans text when dequeued)
    speakQueue.push({ text, playIcon, resolveCallback, voice });
}

/**
 * Strip HTML tags from text, preserving just the readable content.
 */
function stripHTML(text) {
    if (!text) return '';
    // Use a temporary DOM element to extract text content safely
    const tmp = document.createElement('div');
    tmp.innerHTML = text;
    return tmp.textContent || tmp.innerText || '';
}

/**
 * Remove emoji and other non-speech Unicode symbols from text before TTS.
 * Covers: emoticons, misc symbols, supplemental symbols, flags, pictographs.
 */
function stripEmojis(text) {
    if (!text) return '';
    return text
        .replace(/(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}